/**
 * Financial Goals Calculations Utility
 * Provides functions for calculating goal progress and projections
 */

/**
 * Calculate goal progress percentage
 * @param {number} currentAmount - Current savings toward goal
 * @param {number} targetAmount - Target amount for goal
 * @returns {number} Progress percentage (0-100)
 */
export const calculateGoalProgress = (currentAmount, targetAmount) => {
  if (!targetAmount || targetAmount <= 0) return 0;
  const progress = (currentAmount / targetAmount) * 100;
  return Math.min(Math.max(progress, 0), 100); // Clamp between 0-100
};

/**
 * Calculate months until target date
 * @param {string|Date} targetDate - Target date for goal
 * @returns {number} Months remaining (negative if past due)
 */
export const calculateMonthsRemaining = (targetDate) => {
  if (!targetDate) return null;

  const target = new Date(targetDate);
  const now = new Date();

  // Calculate difference in months
  const yearDiff = target.getFullYear() - now.getFullYear();
  const monthDiff = target.getMonth() - now.getMonth();

  return yearDiff * 12 + monthDiff;
};

/**
 * Calculate required monthly savings to reach goal
 * @param {Object} params
 * @param {number} params.currentAmount - Current savings
 * @param {number} params.targetAmount - Target amount
 * @param {string|Date} params.targetDate - Target date
 * @param {number} params.expectedReturn - Expected annual return (percentage)
 * @returns {number} Required monthly savings
 */
export const calculateRequiredMonthlySavings = ({
  currentAmount = 0,
  targetAmount,
  targetDate,
  expectedReturn = 0
}) => {
  const monthsRemaining = calculateMonthsRemaining(targetDate);

  if (!monthsRemaining || monthsRemaining <= 0) return null;

  const amountNeeded = targetAmount - currentAmount;

  // If no expected return, simple division
  if (expectedReturn === 0) {
    return amountNeeded / monthsRemaining;
  }

  // With expected return, use future value of annuity formula
  // FV = PMT × [((1 + r)^n - 1) / r]
  // Solving for PMT: PMT = FV × [r / ((1 + r)^n - 1)]

  const monthlyRate = expectedReturn / 100 / 12;
  const futureValueNeeded = amountNeeded;

  const denominator = Math.pow(1 + monthlyRate, monthsRemaining) - 1;

  if (denominator === 0) {
    return futureValueNeeded / monthsRemaining;
  }

  return (futureValueNeeded * monthlyRate) / denominator;
};

/**
 * Project future value with regular contributions
 * @param {Object} params
 * @param {number} params.currentAmount - Starting amount
 * @param {number} params.monthlyContribution - Monthly contribution
 * @param {number} params.months - Number of months
 * @param {number} params.expectedReturn - Expected annual return (percentage)
 * @returns {number} Projected future value
 */
export const projectFutureValue = ({
  currentAmount = 0,
  monthlyContribution = 0,
  months,
  expectedReturn = 0
}) => {
  if (!months || months <= 0) return currentAmount;

  const monthlyRate = expectedReturn / 100 / 12;

  // Future value of current amount
  const fvOfPresent = currentAmount * Math.pow(1 + monthlyRate, months);

  // Future value of monthly contributions (annuity)
  let fvOfContributions = 0;
  if (monthlyContribution > 0 && monthlyRate > 0) {
    fvOfContributions =
      monthlyContribution * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);
  } else if (monthlyContribution > 0) {
    fvOfContributions = monthlyContribution * months;
  }

  return fvOfPresent + fvOfContributions;
};

/**
 * Determine goal status
 * @param {Object} goal
 * @returns {Object} Status information
 */
export const getGoalStatus = (goal) => {
  const progress = calculateGoalProgress(
    goal.currentAmount || 0,
    goal.targetAmount
  );
  const monthsRemaining = calculateMonthsRemaining(goal.targetDate);

  let status = 'on-track';
  let statusText = 'On Track';
  let color = '#10b981'; // green

  if (progress >= 100) {
    status = 'completed';
    statusText = 'Completed';
    color = '#10b981';
  } else if (monthsRemaining !== null && monthsRemaining <= 0) {
    status = 'overdue';
    statusText = 'Overdue';
    color = '#ef4444';
  } else if (monthsRemaining !== null && monthsRemaining <= 3) {
    status = 'urgent';
    statusText = 'Urgent';
    color = '#f59e0b';
  } else if (progress < 25 && monthsRemaining !== null && monthsRemaining <= 12) {
    status = 'at-risk';
    statusText = 'At Risk';
    color = '#f97316';
  }

  return {
    status,
    statusText,
    color,
    progress,
    monthsRemaining
  };
};

/**
 * Get goal priority score (higher = more urgent)
 * @param {Object} goal
 * @returns {number} Priority score
 */
export const getGoalPriority = (goal) => {
  let score = 0;

  // Factor 1: Time urgency
  const monthsRemaining = calculateMonthsRemaining(goal.targetDate);
  if (monthsRemaining !== null) {
    if (monthsRemaining <= 0) score += 100;
    else if (monthsRemaining <= 3) score += 80;
    else if (monthsRemaining <= 6) score += 60;
    else if (monthsRemaining <= 12) score += 40;
    else score += 20 / Math.log(monthsRemaining);
  }

  // Factor 2: Progress gap
  const progress = calculateGoalProgress(
    goal.currentAmount || 0,
    goal.targetAmount
  );
  score += (100 - progress) / 2;

  // Factor 3: Goal type priority
  const typePriority = {
    'Emergency Fund': 50,
    'Debt Payoff': 45,
    'Home Purchase': 40,
    'Education': 35,
    'Retirement': 30,
    'Investment': 25,
    'Vacation': 15,
    'Other': 20
  };
  score += typePriority[goal.type] || 20;

  return score;
};

/**
 * Sort goals by priority
 * @param {Array} goals
 * @returns {Array} Sorted goals
 */
export const sortGoalsByPriority = (goals = []) => {
  return [...goals].sort((a, b) => {
    const priorityA = getGoalPriority(a);
    const priorityB = getGoalPriority(b);
    return priorityB - priorityA;
  });
};

/**
 * Calculate total allocated amount across all goals
 * @param {Array} goals
 * @returns {number} Total allocated
 */
export const calculateTotalAllocated = (goals = []) => {
  return goals.reduce((total, goal) => {
    return total + (parseFloat(goal.monthlyContribution) || 0);
  }, 0);
};

/**
 * Get goal type options
 * @returns {Array} Goal type options
 */
export const getGoalTypes = () => {
  return [
    'Emergency Fund',
    'Debt Payoff',
    'Home Purchase',
    'Education',
    'Retirement',
    'Investment',
    'Vacation',
    'Wedding',
    'Car Purchase',
    'Business',
    'Other'
  ];
};

/**
 * Validate goal data
 * @param {Object} goal
 * @returns {Object} Validation result
 */
export const validateGoal = (goal) => {
  const errors = [];

  if (!goal.name || goal.name.trim() === '') {
    errors.push('Goal name is required');
  }

  if (!goal.targetAmount || parseFloat(goal.targetAmount) <= 0) {
    errors.push('Target amount must be greater than 0');
  }

  if (!goal.targetDate) {
    errors.push('Target date is required');
  } else {
    const monthsRemaining = calculateMonthsRemaining(goal.targetDate);
    if (monthsRemaining !== null && monthsRemaining < 0) {
      errors.push('Target date cannot be in the past');
    }
  }

  if (goal.currentAmount && parseFloat(goal.currentAmount) < 0) {
    errors.push('Current amount cannot be negative');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Format months as readable duration
 * @param {number} months
 * @returns {string} Formatted duration
 */
export const formatMonthsDuration = (months) => {
  if (months === null) return 'N/A';
  if (months < 0) return 'Overdue';
  if (months === 0) return 'This month';
  if (months === 1) return '1 month';
  if (months < 12) return `${months} months`;

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (remainingMonths === 0) {
    return years === 1 ? '1 year' : `${years} years`;
  }

  return `${years}y ${remainingMonths}m`;
};
